// file: client/src/pages/fournisseur/PortalUI.jsx
/**
 * PHARMAKON — Fournisseur Portal: Premium UI Primitives
 * Redesigned with professional, minimal aesthetics
 */
import { ICONS } from "../../config/theme.js";

// ─── Extended icon set ───────────────────────────────────────────────────────
const EXTRA_ICONS = {
  mail: "M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2zM22 6l-10 7L2 6",
  phone:
    "M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.6a19.79 19.79 0 01-3.07-8.65A2 2 0 012 .82h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.91 8.93a16 16 0 006.16 6.16l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z",
  trending: "M23 6l-9.5 9.5-5-5L1 18",
};

// ─── SVG Icon ─────────────────────────────────────────────────────────────────
export function SvgIco({ n, s = 14, color }) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={s}
      height={s}
      fill="none"
      stroke={color || "currentColor"}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ flexShrink: 0 }}
    >
      <path d={ICONS[n] || EXTRA_ICONS[n] || ICONS.chart} />
    </svg>
  );
}

// ─── Spinner with improved visual ─────────────────────────────────────────────
export function Spinner({ s = 16, color = "#00D264" }) {
  return (
    <div
      style={{
        width: s,
        height: s,
        border: `2.5px solid ${color}22`,
        borderTopColor: color,
        borderRadius: "50%",
        animation: "fp-spin .55s linear infinite",
        flexShrink: 0,
      }}
    />
  );
}

// ─── Skeleton loader ───────────────────────────────────────────────────────────
export function Skeleton({ width = "100%", height = 20, radius = 6 }) {
  return (
    <div
      style={{
        width,
        height,
        borderRadius: radius,
        background:
          "linear-gradient(90deg, #f3f4f6 25%, #f9fafb 50%, #f3f4f6 75%)",
        backgroundSize: "200% 100%",
        animation: "shimmer 1.3s ease-in-out infinite",
      }}
    />
  );
}

// ─── Status Badge with refined design ─────────────────────────────────────────
export function StatutBadge({ statut, meta }) {
  const m = meta?.[statut] ?? {
    label: statut,
    color: "#6b7280",
    bg: "#f3f4f6",
  };
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        padding: "3px 9px",
        borderRadius: 6,
        fontSize: 10.5,
        fontWeight: 700,
        color: m.color,
        background: m.bg,
        whiteSpace: "nowrap",
        letterSpacing: "0.01em",
      }}
    >
      {m.icon && <span style={{ fontSize: 11, lineHeight: 1 }}>{m.icon}</span>}
      {m.label}
    </span>
  );
}

// ─── KPI Card with refined visual hierarchy ───────────────────────────────────
export function KpiCard({
  label,
  value,
  sub,
  color = "#111827",
  icon,
  trend,
  loading,
}) {
  return (
    <div className="kpi-card">
      {loading ? (
        <>
          <Skeleton width="60%" height={12} />
          <div style={{ marginTop: 12, marginBottom: 8 }}>
            <Skeleton width="70%" height={28} />
          </div>
          <Skeleton width="50%" height={10} />
        </>
      ) : (
        <>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              marginBottom: 12,
            }}
          >
            {icon && (
              <div
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: 8,
                  background: `${color}15`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 16,
                  lineHeight: 1,
                }}
              >
                {icon}
              </div>
            )}
            <span className="kpi-label">{label}</span>
          </div>
          <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
            <div className="kpi-value" style={{ color }}>
              {value}
            </div>
            {trend != null && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 3,
                  fontSize: 11,
                  fontWeight: 700,
                  color: trend >= 0 ? "#00D264" : "#dc2626",
                }}
              >
                <SvgIco n={trend >= 0 ? "trending" : "trending"} s={12} />
                {Math.abs(trend)}%
              </div>
            )}
          </div>
          {sub && <div className="kpi-sub">{sub}</div>}
        </>
      )}
    </div>
  );
}

// ─── Mini Progress Bar with smooth animation ──────────────────────────────────
export function MiniBar({ value, max, color = "#00D264", height = 5 }) {
  const pct = max > 0 ? Math.min(100, (value / max) * 100) : 0;
  return (
    <div
      style={{
        height,
        background: "#f1f5f9",
        borderRadius: height / 2,
        overflow: "hidden",
        flex: 1,
      }}
    >
      <div
        style={{
          height: "100%",
          width: `${pct}%`,
          background: `linear-gradient(90deg, ${color}, ${color}dd)`,
          borderRadius: height / 2,
          transition: "width .6s cubic-bezier(0.4, 0, 0.2, 1)",
        }}
      />
    </div>
  );
}

// ─── Toast with improved styling ──────────────────────────────────────────────
export function Toast({ msg, type = "success", onClose }) {
  const styles = {
    success: { bg: "#ecfdf5", border: "#6ee7b7", color: "#047857", icon: "✓" },
    error: { bg: "#fef2f2", border: "#fca5a5", color: "#dc2626", icon: "✕" },
    warning: { bg: "#fffbeb", border: "#fcd34d", color: "#b45309", icon: "⚠" },
  };

  const style = styles[type] || styles.success;

  return (
    <div
      style={{
        position: "fixed",
        bottom: 24,
        right: 24,
        zIndex: 9999,
        padding: "13px 20px",
        borderRadius: 10,
        fontSize: 13,
        fontWeight: 600,
        background: style.bg,
        border: `1.5px solid ${style.border}`,
        color: style.color,
        boxShadow: "0 10px 40px rgba(0,0,0,.12)",
        display: "flex",
        alignItems: "center",
        gap: 12,
        animation: "slideInUp .25s cubic-bezier(0.4, 0, 0.2, 1) both",
        maxWidth: 420,
      }}
    >
      <span style={{ fontSize: 16, lineHeight: 1 }}>{style.icon}</span>
      <span style={{ flex: 1 }}>{msg}</span>
      <button
        onClick={onClose}
        style={{
          background: "none",
          border: "none",
          cursor: "pointer",
          fontSize: 18,
          color: "inherit",
          padding: 0,
          lineHeight: 1,
          opacity: 0.6,
          transition: "opacity .15s",
        }}
        onMouseEnter={(e) => (e.currentTarget.style.opacity = "1")}
        onMouseLeave={(e) => (e.currentTarget.style.opacity = "0.6")}
      >
        ×
      </button>
    </div>
  );
}

// ─── QR Code Display ──────────────────────────────────────────────────────────
export function QRDisplay({ qr_code, numero }) {
  const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(`pharmakon:efacture:${qr_code}`)}`;
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 14,
        padding: "24px 22px",
        background: "#f9fafb",
        border: "1px solid #e5e7eb",
        borderRadius: 14,
      }}
    >
      <div
        style={{
          fontSize: 10,
          fontWeight: 700,
          color: "#9ca3af",
          textTransform: "uppercase",
          letterSpacing: ".8px",
        }}
      >
        QR Code · {numero}
      </div>
      <img
        src={qrUrl}
        alt={`QR ${numero}`}
        width={160}
        height={160}
        style={{
          borderRadius: 8,
          background: "#fff",
          padding: 8,
          border: "1px solid #e5e7eb",
        }}
      />
      <div
        style={{
          fontFamily: "'JetBrains Mono',monospace",
          fontSize: 9,
          color: "#9ca3af",
          letterSpacing: "-.3px",
        }}
      >
        {qr_code}
      </div>
    </div>
  );
}

// ─── Progress Ring with smoother animation ────────────────────────────────────
export function ProgressRing({
  pct,
  size = 64,
  stroke = 6,
  color = "#00D264",
  label,
}) {
  const r = (size - stroke * 2) / 2;
  const circ = 2 * Math.PI * r;
  const off = circ - (pct / 100) * circ;
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 6,
      }}
    >
      <svg width={size} height={size}>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke="#f1f5f9"
          strokeWidth={stroke}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke={color}
          strokeWidth={stroke}
          strokeDasharray={circ}
          strokeDashoffset={off}
          strokeLinecap="round"
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
          style={{
            transition: "stroke-dashoffset .7s cubic-bezier(0.4, 0, 0.2, 1)",
          }}
        />
        <text
          x="50%"
          y="50%"
          dominantBaseline="middle"
          textAnchor="middle"
          style={{
            fontSize: 15,
            fontWeight: 800,
            fontFamily: "'JetBrains Mono',monospace",
            fill: color,
          }}
        >
          {pct}%
        </text>
      </svg>
      {label && (
        <div
          style={{
            fontSize: 10,
            fontWeight: 700,
            color: "#9ca3af",
            textTransform: "uppercase",
            letterSpacing: ".6px",
          }}
        >
          {label}
        </div>
      )}
    </div>
  );
}

// ─── Empty State with improved design ─────────────────────────────────────────
export function EmptyState({ icon = "📭", title, sub, action }) {
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        padding: "72px 24px",
        color: "#9ca3af",
      }}
    >
      <div
        style={{ fontSize: 48, marginBottom: 16, lineHeight: 1, opacity: 0.9 }}
      >
        {icon}
      </div>
      <div
        style={{
          fontSize: 15,
          fontWeight: 800,
          color: "#374151",
          marginBottom: 8,
        }}
      >
        {title}
      </div>
      {sub && (
        <div
          style={{
            fontSize: 13,
            color: "#9ca3af",
            marginBottom: action ? 20 : 0,
            textAlign: "center",
            maxWidth: 320,
          }}
        >
          {sub}
        </div>
      )}
      {action && (
        <button
          onClick={action.onClick}
          className="p-btn p-btn-primary"
          style={{ marginTop: 8 }}
        >
          <SvgIco n="plus" s={13} color="#fff" />
          {action.label}
        </button>
      )}
    </div>
  );
}

// ─── Loading Center ───────────────────────────────────────────────────────────
export function LoadingCenter({ size = 32 }) {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        padding: 72,
      }}
    >
      <Spinner s={size} />
    </div>
  );
}

// ─── Pagination Bar with improved UX ──────────────────────────────────────────
export function Pagination({
  page,
  pages: pagesProp,
  total,
  limit = 20,
  onPage,
}) {
  const pages = pagesProp ?? Math.ceil(total / limit);

  const canPrev = page > 1;
  const canNext = page < pages;

  return (
    <div className="p-pagination">
      <button
        className="p-page-btn"
        disabled={!canPrev}
        onClick={() => onPage(page - 1)}
        title="Page précédente"
      >
        <SvgIco n="chevronLeft" s={11} />
        Préc.
      </button>

      <div
        style={{
          flex: 1,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          gap: 6,
        }}
      >
        <span style={{ fontSize: 11.5, color: "#374151", fontWeight: 600 }}>
          Page {page}
        </span>
        <span style={{ fontSize: 11, color: "#d1d5db" }}>/</span>
        <span style={{ fontSize: 11, color: "#9ca3af" }}>{pages || 1}</span>
        <span style={{ fontSize: 11, color: "#d1d5db", margin: "0 4px" }}>
          ·
        </span>
        <span style={{ fontSize: 10.5, color: "#9ca3af" }}>{total} total</span>
      </div>

      <button
        className="p-page-btn"
        disabled={!canNext}
        onClick={() => onPage(page + 1)}
        title="Page suivante"
      >
        Suiv.
        <SvgIco n="chevronRight" s={11} />
      </button>
    </div>
  );
}

// ─── Premium Portal CSS ──────────────────────────────────────────────────────
export const PORTAL_CSS = `
@import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700;800;900&family=JetBrains+Mono:wght@400;500;600;700;800&display=swap');

@keyframes fp-spin { to { transform: rotate(360deg); } }
@keyframes slideInUp { 
  from { opacity: 0; transform: translateY(10px); } 
  to { opacity: 1; transform: none; } 
}
@keyframes shimmer {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

*,*::before,*::after { box-sizing: border-box; margin: 0; padding: 0; }
::-webkit-scrollbar { width: 6px; height: 6px; }
::-webkit-scrollbar-track { background: #f9fafb; }
::-webkit-scrollbar-thumb { background: #d1d5db; border-radius: 3px; }
::-webkit-scrollbar-thumb:hover { background: #9ca3af; }
body { font-family: 'Outfit', sans-serif; -webkit-font-smoothing: antialiased; }

/* ── Layout ── */
.portal-root { display: flex; height: 100vh; overflow: hidden; background: #f8fafc; font-family: 'Outfit', sans-serif; }
.portal-sidebar { width: 240px; background: linear-gradient(180deg, #0f172a 0%, #1e293b 100%); display: flex; flex-direction: column; flex-shrink: 0; box-shadow: 2px 0 16px rgba(0,0,0,.08); }
.portal-logo { padding: 20px 18px 16px; border-bottom: 1px solid rgba(255,255,255,.08); display: flex; align-items: center; gap: 11px; }
.portal-logo-mark { width: 36px; height: 36px; border-radius: 10px; background: linear-gradient(135deg, #00D264, #00a050); display: flex; align-items: center; justify-content: center; font-size: 18px; flex-shrink: 0; box-shadow: 0 4px 12px rgba(0,210,100,.25); }
.portal-nav { flex: 1; padding: 12px 10px; overflow: auto; }
.portal-nav-item { width: 100%; display: flex; align-items: center; gap: 11px; padding: 10px 14px; border-radius: 10px; margin-bottom: 3px; cursor: pointer; background: transparent; border: 1px solid transparent; color: #94a3b8; font-size: 13px; font-weight: 500; text-align: left; transition: all .15s cubic-bezier(0.4, 0, 0.2, 1); font-family: 'Outfit', sans-serif; }
.portal-nav-item:hover { background: rgba(255,255,255,.06); color: #cbd5e1; }
.portal-nav-item.active { background: rgba(0,210,100,.14); border-color: rgba(0,210,100,.24); color: #00D264; font-weight: 700; box-shadow: 0 2px 8px rgba(0,210,100,.15); }
.portal-user { padding: 14px 16px; border-top: 1px solid rgba(255,255,255,.08); background: rgba(0,0,0,.15); }
.portal-avatar { width: 34px; height: 34px; border-radius: 50%; background: linear-gradient(135deg, rgba(0,210,100,.2), rgba(0,210,100,.15)); border: 2px solid rgba(0,210,100,.3); display: flex; align-items: center; justify-content: center; font-size: 12px; font-weight: 800; color: #00D264; flex-shrink: 0; }
.portal-main { flex: 1; display: flex; flex-direction: column; overflow: hidden; }
.portal-topbar { height: 56px; background: #fff; border-bottom: 1px solid #e5e7eb; display: flex; align-items: center; padding: 0 24px; gap: 14px; flex-shrink: 0; box-shadow: 0 1px 3px rgba(0,0,0,.04); }
.portal-content { flex: 1; overflow: hidden; background: #f8fafc; }

/* ── KPI Cards ── */
.kpi-card { background: #fff; border: 1px solid #e5e7eb; border-radius: 14px; padding: 20px; box-shadow: 0 1px 3px rgba(0,0,0,.06); transition: all .2s cubic-bezier(0.4, 0, 0.2, 1); }
.kpi-card:hover { box-shadow: 0 4px 16px rgba(0,0,0,.08); border-color: #d1d5db; }
.kpi-label { font-size: 10px; font-weight: 700; color: #9ca3af; text-transform: uppercase; letter-spacing: .9px; display: flex; align-items: center; gap: 7px; }
.kpi-value { font-size: 26px; font-weight: 900; font-family: 'JetBrains Mono', monospace; line-height: 1; margin-top: 10px; letter-spacing: -.5px; }
.kpi-sub { font-size: 11.5px; color: #9ca3af; margin-top: 8px; }

/* ── Tables ── */
.p-table { width: 100%; border-collapse: collapse; }
.p-table th { padding: 10px 18px; font-size: 9.5px; font-weight: 700; color: #9ca3af; text-transform: uppercase; letter-spacing: .9px; background: #f9fafb; border-bottom: 2px solid #e5e7eb; text-align: left; white-space: nowrap; position: sticky; top: 0; z-index: 1; }
.p-table td { padding: 12px 18px; font-size: 12.5px; color: #374151; border-bottom: 1px solid #f3f4f6; vertical-align: middle; }
.p-table tbody tr { transition: background .12s cubic-bezier(0.4, 0, 0.2, 1); }
.p-table tbody tr:hover td { background: #f9fafb; }

/* ── Toolbar / Pagination ── */
.p-toolbar { padding: 12px 24px; border-bottom: 1px solid #e5e7eb; background: #fff; display: flex; align-items: center; gap: 12px; flex-shrink: 0; }
.p-pagination { padding: 10px 24px; border-top: 1px solid #e5e7eb; display: flex; align-items: center; gap: 10px; background: #fff; flex-shrink: 0; }
.p-page-btn { padding: 6px 12px; border-radius: 8px; border: 1px solid #e5e7eb; background: #fff; cursor: pointer; font-size: 11.5px; color: #6b7280; font-family: 'Outfit', sans-serif; font-weight: 600; transition: all .15s; display: flex; align-items: center; gap: 5px; }
.p-page-btn:disabled { opacity: 0.4; cursor: default; }
.p-page-btn:not(:disabled):hover { border-color: #00D264; color: #047857; background: #f0fdf4; box-shadow: 0 2px 8px rgba(0,210,100,.12); }

/* ── Inputs ── */
.p-input { background: #f9fafb; border: 1.5px solid #e5e7eb; border-radius: 8px; padding: 8px 12px; font-size: 12.5px; color: #111827; font-family: 'Outfit', sans-serif; outline: none; transition: all .18s; width: 100%; }
.p-input:focus { border-color: #00D264; box-shadow: 0 0 0 4px rgba(0,210,100,.08); background: #fff; }
.p-input::placeholder { color: #d1d5db; }
.p-select { background: #f9fafb; border: 1.5px solid #e5e7eb; border-radius: 8px; padding: 8px 12px; font-size: 12.5px; color: #374151; font-family: 'Outfit', sans-serif; outline: none; cursor: pointer; transition: all .18s; width: 100%; }
.p-select:focus { border-color: #00D264; box-shadow: 0 0 0 4px rgba(0,210,100,.08); background: #fff; }
.p-textarea { background: #f9fafb; border: 1.5px solid #e5e7eb; border-radius: 8px; padding: 10px 12px; font-size: 12px; color: #111827; font-family: 'Outfit', sans-serif; outline: none; transition: all .18s; width: 100%; resize: vertical; }
.p-textarea:focus { border-color: #00D264; box-shadow: 0 0 0 4px rgba(0,210,100,.08); background: #fff; }

/* ── Buttons ── */
.p-btn { display: inline-flex; align-items: center; gap: 7px; border: none; cursor: pointer; border-radius: 8px; font-family: 'Outfit', sans-serif; font-weight: 700; font-size: 12.5px; transition: all .15s cubic-bezier(0.4, 0, 0.2, 1); padding: 8px 16px; white-space: nowrap; }
.p-btn:disabled { opacity: .5; cursor: not-allowed; }
.p-btn-primary { background: linear-gradient(135deg, #00D264, #00a050); color: #fff; box-shadow: 0 2px 10px rgba(0,210,100,.3); }
.p-btn-primary:not(:disabled):hover { filter: brightness(1.08); box-shadow: 0 4px 20px rgba(0,210,100,.4); transform: translateY(-1px); }
.p-btn-primary:not(:disabled):active { transform: translateY(0); }
.p-btn-ghost { background: #fff; color: #6b7280; border: 1.5px solid #e5e7eb; }
.p-btn-ghost:not(:disabled):hover { background: #f9fafb; border-color: #d1d5db; color: #374151; }
.p-btn-danger { background: #fef2f2; color: #dc2626; border: 1.5px solid rgba(220,38,38,.2); }
.p-btn-danger:not(:disabled):hover { background: #fee2e2; border-color: rgba(220,38,38,.3); }
.p-btn-warning { background: #fffbeb; color: #b45309; border: 1.5px solid rgba(180,83,9,.2); }
.p-btn-success { background: #ecfdf5; color: #047857; border: 1.5px solid rgba(4,120,87,.2); }
.p-btn-info { background: #eff6ff; color: #1d4ed8; border: 1.5px solid rgba(29,78,216,.2); }
.p-btn-sm { padding: 5px 11px; font-size: 11.5px; border-radius: 7px; }
.p-btn-lg { padding: 11px 22px; font-size: 13.5px; }

/* ── Form fields ── */
.p-field { display: flex; flex-direction: column; gap: 6px; }
.p-label { font-size: 10px; font-weight: 700; color: #9ca3af; text-transform: uppercase; letter-spacing: .8px; }

/* ── Cards ── */
.p-card { background: #fff; border: 1px solid #e5e7eb; border-radius: 14px; box-shadow: 0 1px 3px rgba(0,0,0,.06); transition: all .2s; }
.p-card:hover { box-shadow: 0 4px 16px rgba(0,0,0,.08); }

/* ── Nav badge ── */
.nav-badge { background: linear-gradient(135deg, #f59e0b, #d97706); color: #fff; border-radius: 999px; font-size: 9.5px; font-weight: 800; padding: 2px 6px; line-height: 14px; margin-left: auto; box-shadow: 0 2px 6px rgba(245,158,11,.3); }
`;
